import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import Table from '@/components/table/Table';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatCurrency } from '@/utils/formatNumber';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RemoveItemIcon from '@/assets/icons/remove-item-icon.svg';
import { CommissionMonthYearOwner } from '@/types/http-payload/commission';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';

interface EditCommissionFormProps {
    idModal: string,
    adjust: CommissionMonthYearOwner,
    onRefresh: Function
}

export default observer(function EditCommissionForm({
    idModal,
    adjust,
    onRefresh
}: EditCommissionFormProps) {
    // hook
    const { t } = useTranslation();

    // store
    const {
        commissionMonthStore: {
            detailCommissionAdjust,
            getCommissionAdjust,
            postCommissionAdjust,
            setObservable
        },
        modalStore: { hideAllModals, showAlertModal }
    } = useStore();

    // form
    // validate schema
    const validateSchema = yup.object().shape({
        commissionAdjusts: yup.array().of(
            yup
                .object()
                .shape({
                    note: yup.string().trim(),
                    amount: yup.string().trim()
                        .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number')).required('form_error_validate.required')
                })
                .nullable()
        )
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            commissionAdjusts: []
        }
    });

    // lifecycle
    useEffect(() => {
        onFetch(adjust);

        return () => {
            setObservable('detailCommissionAdjust', undefined);
        };
    }, [adjust]);

    // columns
    const columns: ColumnDef<any>[] = useMemo(() => {
        return [
            {
                accessorKey: 'id',
                header: t('field.stt'),
                size: 80,
                enableSorting: false,
                cell: ({ row }) => {
                    return row.index + 1;
                }
            },
            {
                accessorKey: 'commissionAdjustsNote',
                header: t('field.content'),
                size: 300,
                enableSorting: false,
                cell: ({ row: { original, index } }) => (
                    <div className=''>
                        <FormField
                            control={form.control}
                            name={`commissionAdjusts.${index}.note`}
                            render={({ field }) => {
                                return (
                                    <FormItem>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder=''
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                className='h-[42px]'
                                            // {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                );
                            }}
                        />
                    </div>
                )
            },
            {
                accessorKey: 'commissionAdjustsAmount',
                header: t('field.fee'),
                size: 300,
                enableSorting: false,
                cell: ({ row: { original, index } }) => (
                    <div className='flex items-center gap-3 h-full'>
                        <FormField
                            control={form.control}
                            name={`commissionAdjusts.${index}.amount`}
                            render={({ field }) => {
                                return (
                                    <FormItem className='w-full'>
                                        <FormControl>
                                            <FormatNumberVer2
                                                decimalScale={0}
                                                statusDisplay='input'
                                                thousandSeparator={true}
                                                allowNegative={true}
                                                value={field.value}
                                                setvalueOfNumericFormat={(values) => {
                                                    field.onChange(values);
                                                }}
                                                className='h-[42px] w-full'
                                                prefix={t('currency.currency_symbol')}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                );
                            }}
                        />
                        <div className='h-full flex items-center'>
                            <button type='button' onClick={() => onClickDeleteItem(index)}>
                                <img src={RemoveItemIcon} alt={t('button.delete')} />
                            </button>
                        </div>

                    </div>
                )
            }
        ];
    }, [detailCommissionAdjust?.commissionAdjusts]);

    // function
    const onFetch = async (payload) => {
        if (!payload) {
            form.setValue('commissionAdjusts', []);
            return;
        }

        const res = await flowResult(getCommissionAdjust(payload));
        if (res) {
            form.reset({
                commissionAdjusts: res?.commissionAdjusts || []
            });
            return;
        }
        form.setValue('commissionAdjusts', []);
    };

    const onCancel = () => {
        hideAllModals();
    };

    const onSubmit = async (data: FormData) => {
        const body = {
            commissionMonth: adjust.commissionMonth,
            commissionYear: adjust.commissionYear,
            ownerId: adjust.ownerId,
            commissionAdjusts: data?.commissionAdjusts?.length ?
                data?.commissionAdjusts.map(item => ({
                    ...item,
                    amount: Number(item?.amount)
                })) :
                []
        };

        const res = await flowResult(postCommissionAdjust(body));
        if (res) {
            onRefresh();
            hideAllModals();
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.update_success')
            });
        }
    };

    const onClickAddItem = () => {
        let currentAdjusts: any[] = form.getValues('commissionAdjusts') || [];

        currentAdjusts.push({
            note: '',
            amount: 0
        });

        form.setValue('commissionAdjusts', [...currentAdjusts]);
        setObservable('detailCommissionAdjust', {
            ...detailCommissionAdjust,
            commissionAdjusts: [...currentAdjusts]
        });
    };

    const onClickDeleteItem = (index: number) => {
        let currentAdjusts: any[] = form.getValues('commissionAdjusts') || [];

        if (currentAdjusts?.[index]) {
            const newAdjusts = currentAdjusts?.filter((_, i) => i !== index);
            form.setValue('commissionAdjusts', [...newAdjusts]);
            setObservable('detailCommissionAdjust', {
                ...detailCommissionAdjust,
                commissionAdjusts: [...newAdjusts]
            });
        }
    };

    // const
    const [totalAdjust, isNegative] = useMemo(() => {
        let totalAdjustAmount = Number((detailCommissionAdjust?.totalAdjustAmount) || '0');
        let negativeNumber = totalAdjustAmount < 0;

        return [totalAdjustAmount, negativeNumber];
    }, [detailCommissionAdjust?.totalAdjustAmount]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='mt-0'
                autoComplete='off'
            >
                <div className='flex flex-col gap-4'>
                    <div className='overflow-auto max-h-[calc(100vh-450px)]'>
                        <Table
                            columns={columns}
                            data={detailCommissionAdjust?.commissionAdjusts || []}
                            enableRowSelection={false}
                            getRowId={row => row.id?.toString()}
                            showSelectPageSize={false}
                            showTableFooter={false}
                            cellClassName='p-2'
                            headerClassName='p-2'
                        />
                    </div>

                    <div>
                        <div className='font-semibold text-[15px] text-primaryWeb flex justify-end items-center'>
                            <span className='whitespace-nowrap'>
                                {`${t('words_title.total_amount')}：`}
                            </span>
                            <span className='break-all'>
                                {
                                    isNegative && <span> &#9650;</span>
                                }
                                <span>
                                    {t('currency.currency_symbol')}
                                    {formatCurrency(Math.abs(totalAdjust))}
                                </span>
                            </span>
                        </div>
                    </div>

                    <div>
                        <ButtonCus
                            type='button'
                            variant='orange3'
                            size='md'
                            className='w-full'
                            onClick={onClickAddItem}
                        >
                            <CirclePlus className='mr-2' />
                            {t('field.adjustment_amount')}
                        </ButtonCus>
                    </div>

                    <div className='flex gap-4 mt-3 justify-center'>
                        <ButtonCus
                            variant='outline'
                            size='inModal'
                            type='button'
                            onClick={onCancel}
                        >
                            {t('button.cancel')}
                        </ButtonCus>
                        <ButtonCusWithIcon
                            type='submit'
                            variant='default'
                            size='inModal'
                            iconId='#icon-update'
                        >
                            {t('button.update')}
                        </ButtonCusWithIcon>
                    </div>
                </div>
            </form>
        </Form>
    );
});
